interface InpAttribution {
  testId?: string;
  componentId?: string;
  elementId?: string;
  tagName?: string;
  className?: string;
  selector?: string;
  text?: string;
}

interface InpPayload {
  timestamp: string;
  eventName: 'cwv_inp';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  interactionType: string;
  inputDelay: number;
  processingTime: number;
  presentationDelay: number;
  url: string;
  pageName?: string;
  userAgent: string;
  deviceType: string;
  isMobile: boolean;
  appId?: string;
  attribution: InpAttribution;
}

const API_ENDPOINT = `${process.env.NEXT_PUBLIC_UBA_AUDIT_URL}/api/cwv-inp-audit`;
const MAX_SELECTOR_DEPTH = 5;
const MAX_TEXT_LEN = 40;

interface WorstInteraction {
  duration: number;
  name: string;
  startTime: number;
  processingStart: number;
  processingEnd: number;
  attribution: InpAttribution;
}

class CwvInpLogger {
  private observer: PerformanceObserver | null = null;
  private worstEntry: WorstInteraction | null = null;
  private initialized = false;
  private pageName?: string;

  /**
   * Start observing INP. Safe to call multiple times — only first call wires up.
   * Defers all work to requestIdleCallback so page-load critical path is untouched.
   */
  start(pageName?: string): void {
    if (this.initialized || typeof window === 'undefined') return;
    if (typeof PerformanceObserver === 'undefined') return;

    this.pageName = pageName;

    const schedule =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (cb: IdleRequestCallback) =>
            setTimeout(() => cb({didTimeout: false, timeRemaining: () => 50}), 1);

    schedule(() => this.init());
  }

  private init(): void {
    if (this.initialized) return;

    const criteria = window?.__FEATURE_FLAGS__?.cwvLoggingCriteria;
    
    if (!criteria?.enableInpLogging) return;

    if (criteria.mobileOnly) {
      const isMobile = /(iPad|iPhone|Android|Mobile)/i.test(navigator.userAgent);
      if (!isMobile) return;
    }

    if (criteria.pageNames?.length && this.pageName) {
      const matches = criteria.pageNames.some(prefix =>
        this.pageName!.startsWith(prefix)
      );
      if (!matches) return;
    }

    const supported =
      PerformanceObserver.supportedEntryTypes?.includes('event') ?? false;
    if (!supported) return;

    this.initialized = true;

    try {
      this.observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries() as PerformanceEventTiming[]) {
          const duration = entry.duration;
          if (duration < 40) continue;
          if (!this.worstEntry || duration > this.worstEntry.duration) {
            // Resolve attribution NOW while the target element is still in the DOM.
            // entry.target becomes null once the element is unmounted (very common in React).
            this.worstEntry = {
              duration,
              name: entry.name,
              startTime: entry.startTime,
              processingStart: entry.processingStart,
              processingEnd: entry.processingEnd,
              attribution: this.attribute(entry.target as Element | null),
            };
          }
        }
      });
      this.observer.observe({
        type: 'event',
        buffered: true,
        durationThreshold: 40,
      } as PerformanceObserverInit);
    } catch {
      return;
    }

    const onHidden = () => {
      if (document.visibilityState === 'hidden') this.report();
    };
    document.addEventListener('visibilitychange', onHidden, {capture: true});
    window.addEventListener('pagehide', () => this.report(), {capture: true});
  }

  /**
   * Send the current page's worst INP and reset.
   * Pass nextPageName when reporting due to an SPA route change.
   * No-op if there's nothing to report — making it safe to call multiple times.
   */
  report(nextPageName?: string): void {
    if (this.worstEntry) {
      try {
        const payload = this.buildPayload(this.worstEntry);
        // Send as plain string so sendBeacon uses text/plain (CORS-safelisted, no preflight).
        // application/json would trigger a preflight that sendBeacon handles unreliably across browsers.
        const body = JSON.stringify({events: [payload]});
        const sent = navigator.sendBeacon(API_ENDPOINT, body);
        if (!sent && typeof fetch === 'function') {
          fetch(API_ENDPOINT, {
            method: 'POST',
            body,
            keepalive: true,
            headers: {'Content-Type': 'text/plain'},
          }).catch(() => {});
        }
      } catch {
        // best effort — data loss acceptable on unload
      }
      this.worstEntry = null;
    }
    if (nextPageName !== undefined) this.pageName = nextPageName;
  }

  private buildPayload(entry: WorstInteraction): InpPayload {
    const userAgent = navigator.userAgent;
    const isMobile = /(iPad|iPhone|Android|Mobile)/i.test(userAgent);
    const deviceType = this.getDeviceOs(userAgent, isMobile);

    const inputDelay = Math.max(0, entry.processingStart - entry.startTime);
    const processingTime = Math.max(
      0,
      entry.processingEnd - entry.processingStart
    );
    const presentationDelay = Math.max(
      0,
      entry.startTime + entry.duration - entry.processingEnd
    );

    return {
      timestamp: new Date().toISOString(),
      eventName: 'cwv_inp',
      value: entry.duration,
      rating: this.rate(entry.duration),
      interactionType: entry.name,
      inputDelay,
      processingTime,
      presentationDelay,
      url: window.location.href,
      pageName: this.pageName,
      userAgent,
      deviceType,
      isMobile,
      attribution: entry.attribution,
    };
  }

  private rate(v: number): 'good' | 'needs-improvement' | 'poor' {
    if (v <= 200) return 'good';
    if (v <= 500) return 'needs-improvement';
    return 'poor';
  }

  private attribute(target: Element | null): InpAttribution {
    if (!target) return {};

    const attribution: InpAttribution = {
      tagName: target.tagName?.toLowerCase(),
      className:
        typeof target.className === 'string'
          ? target.className.slice(0, 120)
          : undefined,
      selector: this.selector(target),
    };

    if (target.id) attribution.elementId = target.id;

    const text = (target as HTMLElement).innerText?.trim();
    if (text) attribution.text = text.slice(0, MAX_TEXT_LEN);

    let node: Element | null = target;
    let depth = 0;
    while (node && depth < MAX_SELECTOR_DEPTH) {
      const testId = node.getAttribute?.('data-testid');
      if (testId && !attribution.testId) attribution.testId = testId;
      const cmp = node.getAttribute?.('data-component');
      if (cmp && !attribution.componentId) attribution.componentId = cmp;
      if (attribution.testId && attribution.componentId) break;
      node = node.parentElement;
      depth++;
    }

    return attribution;
  }

  private selector(el: Element): string {
    const parts: string[] = [];
    let node: Element | null = el;
    let depth = 0;
    while (node && depth < MAX_SELECTOR_DEPTH) {
      let part = node.tagName.toLowerCase();
      if (node.id) {
        part += `#${node.id}`;
        parts.unshift(part);
        break;
      }
      if (typeof node.className === 'string' && node.className) {
        const cls = node.className.trim().split(/\s+/).slice(0, 2).join('.');
        if (cls) part += `.${cls}`;
      }
      parts.unshift(part);
      node = node.parentElement;
      depth++;
    }
    return parts.join('>');
  }

  private getDeviceOs(userAgent: string, isMobile: boolean): string {
    if (isMobile) {
      if (/android/i.test(userAgent)) return 'android';
      if (/iphone|ipad|ipod|ios/i.test(userAgent)) return 'ios';
    }
    return 'web';
  }
}

const cwvInpLogger = new CwvInpLogger();

export default cwvInpLogger;
